import { useState, useMemo, useRef, useEffect } from 'react';
import { T } from '../theme';
import { Filter, X, ArrowUp, ArrowDown, Layers, Calendar } from 'lucide-react';
import DateRangePicker, { detectPreset, PRESET_LIST } from './DateRangePicker';

// ─── Exported styles for filter field rows ────────────────────────────────────

export const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
};

export const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 8,
  border: '1px solid #e5e7eb', fontSize: 13, color: '#374151',
  background: '#fff', boxSizing: 'border-box', outline: 'none',
  colorScheme: 'light',
};

// ─── Internal styles ──────────────────────────────────────────────────────────

const panelStyle = {
  background: '#f9fafb', border: '1px solid #e5e7eb',
  borderRadius: 12, padding: '14px 16px',
};

// ─── Component ────────────────────────────────────────────────────────────────
//
// Props:
//   sortOptions   [{value, label, defaultDir?}]  — defaultDir defaults to 'desc'
//   sortBy / sortDir / onSortChange(value, dir)
//   dateFrom / dateTo / onDateChange(from, to)
//   activeFilterCount / onClearFilters / filterFields  — JSX for filter rows
//   groupBy / groupSections [{label?, options:[{value,label}]}] / onGroupChange(value)

export default function SortFilterBar({
  sortOptions = [],
  sortBy,
  sortDir,
  onSortChange,
  dateFrom = '',
  dateTo   = '',
  onDateChange,
  activeFilterCount = 0,
  onClearFilters,
  filterFields,
  groupBy,
  groupSections = [],
  onGroupChange,
}) {
  const [showDatePanel,  setShowDatePanel]  = useState(false);
  const [showFilters,    setShowFilters]    = useState(false);
  const [showGroupPanel, setShowGroupPanel] = useState(false);

  const dateRef = useRef(null);
  useEffect(() => {
    if (!showDatePanel) return;
    const handler = (e) => { if (dateRef.current && !dateRef.current.contains(e.target)) setShowDatePanel(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDatePanel]);

  const datePresetKey = detectPreset(dateFrom, dateTo);
  const datePillLabel = PRESET_LIST.find(p => p.key === datePresetKey)?.label ?? 'Custom';

  const groupActiveLabel = useMemo(() => {
    if (groupBy == null) return null;
    for (const section of groupSections) {
      const opt = section.options.find(o => o.value === groupBy);
      if (opt) return opt.label;
    }
    return null;
  }, [groupBy, groupSections]);

  const toggleDate  = () => { setShowDatePanel(v => !v); setShowFilters(false);    setShowGroupPanel(false); };
  const toggleFilter= () => { setShowFilters(v => !v);   setShowDatePanel(false);  setShowGroupPanel(false); };
  const toggleGroup = () => { setShowGroupPanel(v => !v);setShowFilters(false);    setShowDatePanel(false);  };

  const dateActive   = showDatePanel  || datePresetKey !== 'all_time';
  const filterActive = showFilters    || activeFilterCount > 0;
  const groupActive  = showGroupPanel || groupActiveLabel != null;

  return (
    <div>
      {/* ── Toolbar strip ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #F3F4F6', padding: '8px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>

          {/* Date range */}
          <div ref={dateRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              onClick={toggleDate}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 8,
                border: `1.5px solid ${dateActive ? T.tealDeep : '#e5e7eb'}`,
                background: dateActive ? T.tealDeep : '#fff',
                color: dateActive ? '#fff' : '#6B7280',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <Calendar size={10} />
              {datePillLabel}
            </button>
            {showDatePanel && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 200,
                background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                padding: '4px 10px 6px', minWidth: 190,
              }}>
                <DateRangePicker
                  variant="light"
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  onChange={(from, to) => { onDateChange(from, to); setShowDatePanel(false); }}
                />
              </div>
            )}
          </div>

          {/* Sort pills */}
          {sortOptions.map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                if (sortBy === opt.value) onSortChange(opt.value, sortDir === 'asc' ? 'desc' : 'asc');
                else onSortChange(opt.value, opt.defaultDir ?? 'desc');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '4px 10px', borderRadius: 8, border: '1.5px solid',
                borderColor: sortBy === opt.value ? T.tealDeep : '#e5e7eb',
                background:  sortBy === opt.value ? T.tealDeep : '#fff',
                color:       sortBy === opt.value ? '#fff' : '#6B7280',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {opt.label}
              {sortBy === opt.value && (sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
            </button>
          ))}

          {/* Filter */}
          <button
            onClick={toggleFilter}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 8,
              border: `1.5px solid ${filterActive ? T.tealDeep : '#e5e7eb'}`,
              background: filterActive ? T.tealDeep : '#fff',
              color: filterActive ? '#fff' : '#6B7280',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Filter size={10} />
            Filter
            {activeFilterCount > 0 && (
              <span style={{
                background: '#fff', color: T.tealDeep, borderRadius: '50%',
                width: 15, height: 15, fontSize: 9, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Group */}
          <button
            onClick={toggleGroup}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 8,
              border: `1.5px solid ${groupActive ? T.tealBright : '#e5e7eb'}`,
              background: groupActive ? T.tealBright : '#fff',
              color: groupActive ? '#fff' : '#6B7280',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Layers size={10} />
            {groupActiveLabel ?? 'Group'}
          </button>

        </div>
      </div>

      {/* ── Group panel ── */}
      {showGroupPanel && (
        <div style={{ padding: '12px 16px' }}>
          <div style={panelStyle}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Group By</div>
            {groupSections.map((section, i) => (
              <div key={i} style={i > 0 ? { marginTop: 10 } : {}}>
                {section.label && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                    {section.label}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {section.options.map(opt => (
                    <button
                      key={String(opt.value)}
                      onClick={() => { onGroupChange(opt.value); setShowGroupPanel(false); }}
                      style={{
                        padding: '4px 12px', borderRadius: 20,
                        border: `1.5px solid ${groupBy === opt.value ? T.tealBright : '#e5e7eb'}`,
                        background: groupBy === opt.value ? T.tealBright : '#fff',
                        color: groupBy === opt.value ? '#fff' : '#374151',
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filter panel ── */}
      {showFilters && (
        <div style={{ padding: '12px 16px' }}>
          <div style={panelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Filters</span>
              {activeFilterCount > 0 && (
                <button
                  onClick={onClearFilters}
                  style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', color: T.tealDeep, fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                >
                  <X size={11} /> Clear all
                </button>
              )}
            </div>
            {filterFields}
          </div>
        </div>
      )}
    </div>
  );
}
